import React, { useState, useEffect, useCallback } from "react";
import {
  Search,
  Filter,
  Clock,
  Star,
  ChevronDown,
  ChevronUp,
  Calendar,
  Tag,
  MessageCircle,
  CheckCircle,
} from "lucide-react";
import { ticketService } from "../services/ticketService";
import {
  TicketSearchRequest,
  TicketSearchResult,
  TicketCategory,
} from "../types/tickets";

const TicketSearchInterface: React.FC = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TicketSearchResult[]>([]);
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");
  const [minConfidence, setMinConfidence] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [searchTime, setSearchTime] = useState<number>(0);
  const [totalFound, setTotalFound] = useState<number>(0);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedResults, setExpandedResults] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    // Fetch categories for filtering
    const fetchCategories = async () => {
      try {
        const response = await ticketService.getTicketCategories();
        setCategories(response.categories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;

    setLoading(true);
    try {
      const request: TicketSearchRequest = {
        query: query.trim(),
        limit: 20,
        category_filter:
          selectedCategories.length > 0 ? selectedCategories : undefined,
        date_range_start: dateRangeStart || undefined,
        date_range_end: dateRangeEnd || undefined,
        min_confidence: minConfidence > 0 ? minConfidence / 100 : undefined,
      };

      const response = await ticketService.searchTickets(request);
      setResults(response.results);
      setSearchTime(response.search_time_ms);
      setTotalFound(response.total_found);
    } catch (error) {
      console.error("Error searching tickets:", error);
      setResults([]);
      setTotalFound(0);
    } finally {
      setLoading(false);
    }
  }, [query, selectedCategories, dateRangeStart, dateRangeEnd, minConfidence]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const toggleCategoryFilter = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setDateRangeStart("");
    setDateRangeEnd("");
    setMinConfidence(0);
  };

  const toggleResultExpansion = (ticketId: string) => {
    setExpandedResults((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ticketId)) {
        newSet.delete(ticketId);
      } else {
        newSet.add(ticketId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getConfidenceColor = (confidence?: number) => {
    if (!confidence) return "text-neutral-400";
    if (confidence >= 0.8) return "text-green-500";
    if (confidence >= 0.6) return "text-yellow-500";
    return "text-red-500";
  };

  const getSimilarityColor = (similarity: number) => {
    if (similarity >= 0.8)
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
    if (similarity >= 0.6)
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
  };

  return (
    <div className="space-y-6">
      {/* Search Header */}
      <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
            <input
              type="text"
              placeholder="Search tickets by keywords, issues, or solutions..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full pl-10 pr-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-3 border rounded-lg transition-colors ${
                showFilters ||
                selectedCategories.length > 0 ||
                dateRangeStart ||
                dateRangeEnd ||
                minConfidence > 0
                  ? "border-orange-500 text-orange-600 bg-orange-50 dark:bg-orange-900/20"
                  : "border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {showFilters ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={handleSearch}
              disabled={loading || !query.trim()}
              className="flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white rounded-lg transition-colors"
            >
              <Search className="w-4 h-4" />
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700 space-y-4">
            {/* Categories Filter */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Categories
              </label>
              <div className="flex flex-wrap gap-2">
                {categories.slice(0, 12).map((category) => (
                  <button
                    key={category.name}
                    onClick={() => toggleCategoryFilter(category.name)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedCategories.includes(category.name)
                        ? "bg-orange-500 text-white"
                        : "bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-600"
                    }`}
                  >
                    {category.name} ({category.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Date From
                </label>
                <input
                  type="date"
                  value={dateRangeStart}
                  onChange={(e) => setDateRangeStart(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Date To
                </label>
                <input
                  type="date"
                  value={dateRangeEnd}
                  onChange={(e) => setDateRangeEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Min Confidence ({minConfidence}%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={minConfidence}
                  onChange={(e) => setMinConfidence(Number(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>

            {/* Clear Filters */}
            <div className="flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
              >
                Clear all filters
              </button>
            </div>
          </div>
        )}

        {/* Search Stats */}
        {(results.length > 0 || totalFound > 0) && (
          <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400">
              <span>
                Found {totalFound} tickets in {searchTime.toFixed(1)}ms
              </span>
              <span>Showing {results.length} results</span>
            </div>
          </div>
        )}
      </div>

      {/* Search Results */}
      {results.length > 0 && (
        <div className="space-y-4">
          {results.map((result) => {
            const isExpanded = expandedResults.has(result.ticket_id);
            return (
              <div
                key={result.ticket_id}
                className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6 hover:shadow-lg transition-shadow"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">
                        {result.subject}
                      </h3>
                      <div
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getSimilarityColor(
                          result.similarity_score
                        )}`}
                      >
                        {Math.round(result.similarity_score * 100)}% match
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-neutral-500 dark:text-neutral-400">
                      <span className="flex items-center gap-1">
                        <Tag className="w-4 h-4" />
                        {result.issue_category}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(result.created_time)}
                      </span>
                      {result.ticket_number && (
                        <span>#{result.ticket_number}</span>
                      )}
                      {result.confidence_score && (
                        <span
                          className={`flex items-center gap-1 ${getConfidenceColor(
                            result.confidence_score
                          )}`}
                        >
                          <Star className="w-4 h-4" />
                          {Math.round(result.confidence_score * 100)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => toggleResultExpansion(result.ticket_id)}
                    className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Summary */}
                <div className="mb-4">
                  <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
                    {result.summary}
                  </p>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="space-y-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                    {/* Customer Query */}
                    <div>
                      <h4 className="flex items-center gap-2 font-medium text-neutral-900 dark:text-neutral-100 mb-2">
                        <MessageCircle className="w-4 h-4 text-blue-500" />
                        Customer Query
                      </h4>
                      <p className="text-neutral-700 dark:text-neutral-300 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                        {result.customer_query}
                      </p>
                    </div>

                    {/* Agent Solution */}
                    <div>
                      <h4 className="flex items-center gap-2 font-medium text-neutral-900 dark:text-neutral-100 mb-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        Solution Provided
                      </h4>
                      <p className="text-neutral-700 dark:text-neutral-300 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                        {result.agent_solution}
                      </p>
                    </div>

                    {/* Resolution Time */}
                    {result.resolved_time && (
                      <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
                        <Clock className="w-4 h-4" />
                        Resolved on {formatDate(result.resolved_time)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && results.length === 0 && query && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-12 text-center">
          <Search className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
            No tickets found
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400">
            Try adjusting your search terms or filters to find relevant tickets.
          </p>
        </div>
      )}

      {/* Initial State */}
      {!query && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-12 text-center">
          <Search className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
            Search Support Tickets
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            Find solutions from past support cases using natural language
            search.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              "login issues",
              "password reset",
              "billing questions",
              "timesheet problems",
            ].map((example) => (
              <button
                key={example}
                onClick={() => setQuery(example)}
                className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-sm hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketSearchInterface;
